import { ArrowUpRight, Users } from 'lucide-react'
import { research, type ResearchItem } from '@/data/site'
import { SectionHeading } from '@/components/ui/section-heading'
import { SpotlightCard } from '@/components/ui/spotlight-card'
import { Reveal } from '@/components/ui/reveal'
import { GithubIcon } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

const accent: Record<ResearchItem['accent'], { rgb: string; text: string; bar: string }> = {
  blush: { rgb: '255 179 217', text: 'text-blush', bar: 'from-blush/80' },
  lilac: { rgb: '196 181 253', text: 'text-lilac', bar: 'from-lilac/80' },
  sky: { rgb: '135 206 235', text: 'text-sky', bar: 'from-sky/80' },
}

export function Research() {
  return (
    <section id="research" aria-labelledby="research-title" className="relative py-24 md:py-32">
      <div className="container">
        <SectionHeading
          id="research-title"
          eyebrow="Research projects"
          title="Applied machine learning, end to end."
          description="Projects where I take a messy real-world question (a diagnosis, a contamination source, a network intent) and build the models to answer it."
        />

        <div className="grid gap-4 lg:grid-cols-3">
          {research.map((item, idx) => {
            const a = accent[item.accent]
            return (
              <Reveal key={item.id} delay={idx * 0.08} className="h-full">
                <SpotlightCard glow={a.rgb} className="h-full">
                  <article className="flex h-full flex-col p-7">
                    <div className={cn('mb-6 h-px w-16 bg-gradient-to-r to-transparent', a.bar)} aria-hidden="true" />
                    <p className={cn('font-mono text-[11px] uppercase tracking-[0.18em]', a.text)}>{item.category}</p>
                    <h3 className="mt-3 text-2xl font-medium tracking-tight text-white">{item.title}</h3>
                    <p className="mt-4 text-pretty leading-relaxed text-zinc-400">{item.summary}</p>

                    <ul className="mt-6 space-y-2.5">
                      {item.highlights.map((h) => (
                        <li key={h} className="flex gap-3 text-sm text-zinc-300">
                          <span className={cn('mt-[7px] h-1 w-1 shrink-0 rounded-full bg-current', a.text)} aria-hidden="true" />
                          {h}
                        </li>
                      ))}
                    </ul>

                    <ul className="mt-6 flex flex-wrap gap-2" aria-label="Topics">
                      {item.tags.map((t) => (
                        <li key={t} className="chip">
                          {t}
                        </li>
                      ))}
                    </ul>

                    <div className="flex-1" aria-hidden="true" />
                    <div className="mt-8 flex items-center justify-between gap-4 border-t border-white/[0.06] pt-5">
                      {item.contributors ? (
                        <span className="flex items-center gap-2 text-xs text-zinc-500">
                          <Users className="h-3.5 w-3.5" /> with {item.contributors.join(', ')}
                        </span>
                      ) : (
                        <span />
                      )}
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${item.title} source code on GitHub`}
                        className="group/link inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-zinc-200 transition hover:border-white/20 hover:bg-white/[0.08]"
                      >
                        <GithubIcon className="h-3.5 w-3.5" />
                        Code
                        <ArrowUpRight className="h-3 w-3 transition-transform group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5" />
                      </a>
                    </div>
                  </article>
                </SpotlightCard>
              </Reveal>
            )
          })}
        </div>
      </div>
    </section>
  )
}
